import React, {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
	type RefObject,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useInlineFieldsActions } from '@atlassian/jira-forge-controllers-inline-fields-state/src/index.tsx';
import {
	UIM_CONSENT_REQUIRED,
	useUIModificationsConsentStatus,
} from '@atlassian/ui-modifications-core/controllers';
import FindFieldModal from '@atlassian/jira-issue-config-find-field/src/async.tsx';
import { successFlagDisplay } from '@atlassian/jira-issue-create-analytics/src/services/performance-analytics/index.tsx';
import { REQUEST_TYPE } from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import { ISSUE_CREATE_TYPE } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import type {
	DirtyFields,
	FormState,
	FormFieldValues,
	ClassificationFieldProps,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformProjectType } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-project-type/index.tsx';
import { useClassificationFieldProps } from '@atlassian/jira-issue-create-commons/src/ui/data-classification-field/utils.tsx';
import ConfigureFieldsModal from '@atlassian/jira-issue-create-configure-fields/src/async.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { isMobileAndInMvpOrExistingUsersExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { useSitePickerContext } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/controllers/use-site-picker-context/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { isPartOfDiscoverabilityTriggers } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/utils.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import {
	ContextualAnalyticsData,
	fireScreenAnalyticsDeferred,
	fireUIAnalytics,
	MODAL,
	MountEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { usePersistentIssueCreateModalViewResource } from '@atlassian/jira-router-resources-persistent-issue-create-modal-view/src/controllers/main.tsx';
import { useStorageEnforcement } from '@atlassian/jira-software-enforce-storage-limits/src/controllers/storage-enforcement/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { UiModificationsContainer } from '@atlassian/jira-ui-modifications-view-gic/src/ui/container/index.tsx';
import { UiModificationsEntryPoint } from '@atlassian/jira-ui-modifications-view-gic/src/ui/entry-point/index.tsx';

import { getAdditionalAttributes } from '@atlassian/jira-issue-create-analytics-common/src/services/index.tsx';
import type { CreatedIssue, CreatedIssues } from '../../common/types/index.tsx';
import { navigationDiscardChangesPromptEntrypoint } from '../../common/ui/navigation-discard-changes-prompt/entrypoint.tsx';
import { scrollToField, setFocusOnField } from '../../common/utils/index.tsx';
import { getIssueDiscoveryUrl } from '../../common/utils/issue-discovery/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { useChangerSpotlight } from '../../controllers/changer-spotlight/index.tsx';
import { useIsGICPositioningEnabled } from '../../controllers/gic-positioning-enabled/main.tsx';
import { useNavigationDiscardChanges } from '../../controllers/navigation-discard-changes/index.tsx';
import { useRequestTypeFieldsLayoutPreference } from '../../controllers/request-type-fields-layout-user-preference/index.tsx';
import { useIssueCreateFromAnywhereContext } from '../../controllers/use-issue-create-from-anywhere-context/index.tsx';
import type { IssueCreateResponse } from '../../services/create-issue/types.tsx';
import { useFetchForgeModules } from '../../services/fetch-forge-modules/index.tsx';
import useFetchTransitionsService from '../../services/fetch-transitions/index.tsx';
import useModalLoadQuery from '../../services/modal-load-query/index.tsx';
// import ConfirmDismissModal from './confirm-dismiss-modal';
import { createIssueDismissModalEntryPoint } from './confirm-dismiss-modal/modal/entrypoint.tsx';
import CreateForm from './create-form/index.tsx';
import type { CreateFormProps } from './create-form/types.tsx';
import {
	getQueryVariables,
	shouldShowStatusField,
	getIssueCreateProjectForMount,
	useShowOnlyRequiredFieldsExperiment,
} from './create-form/utils.tsx';
import ErrorState from './error-state/index.tsx';
import { ExistingGICWarning } from './existing-gic-warning/index.tsx';
import { JsmRequestTypeFieldsChangeboarding } from './jsm-request-type-fields-changeboarding/index.tsx';
import { ModalWrapper } from './modal-wrapper/main.tsx';
import { NavigateWithoutRestorePrompt } from './navigate-without-restore-prompt/index.tsx';
import SuccessFlag from './success-flag/index.tsx';
import type { SuccessFlagParams } from './success-flag/types.tsx';
import type { CreateModalProps } from './types.tsx';
import {
	getFirstFieldIdWithError,
	getTabIndexWithError,
	getWatchersFlagConfigFromResponse,
	publishCreatedIssues,
	useStorageVariables,
} from './utils.tsx';

interface DisabledRestoreNavigationPromptProps {
	setShouldRestore?: (shouldRestore: boolean) => void;
}

const DisabledRestoreNavigationPrompt: React.FC<DisabledRestoreNavigationPromptProps> = ({
	setShouldRestore,
}) => {
	const [{ deferredNavigationLink }, { setDeferredNavigationLink }] = useNavigationDiscardChanges();
	const [{ isFormDirty }] = useFormContext();

	const onConfirmHandler = useCallback(() => {
		setShouldRestore && setShouldRestore(false);
	}, [setShouldRestore]);

	const onCancelHandler = useCallback(() => {
		setDeferredNavigationLink('');
	}, [setDeferredNavigationLink]);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={navigationDiscardChangesPromptEntrypoint}
			entryPointProps={{
				navigateLink: isFormDirty ? deferredNavigationLink : '',
				onConfirm: onConfirmHandler,
				onCancel: onCancelHandler,
			}}
			interactionName="navigateWithoutRestorePrompt"
			useInternalModal={false}
		>
			{({ ref }) => <NavigateWithoutRestorePrompt setShouldRestore={setShouldRestore} ref={ref} />}
		</ModalEntryPointPressableTrigger>
	);
};

const DismissModalEntryPointWrapper = ({
	children,
	entryPointProps,
}: {
	children: ReactNode;
	entryPointProps: {
		project: {
			projectId: string;
			projectType: ProjectType;
		};
		sessionId: string;
		onClose: () => void;
		onConfirm: (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	};
}) => (
	<ModalEntryPointPressableTrigger
		entryPoint={createIssueDismissModalEntryPoint}
		entryPointProps={entryPointProps}
		interactionName="create-issue-dismiss-modal"
		modalProps={{ width: 'small' }}
		useInternalModal
	>
		{({ ref }) => (
			<div
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				ref={ref as RefObject<HTMLDivElement>}
			>
				{children}
			</div>
		)}
	</ModalEntryPointPressableTrigger>
);

const CreateModal = ({
	onTransformCreateFormProps,
	setShouldRestore,
	onResetApolloCache,
}: CreateModalProps): React.JSX.Element => {
	const sessionIdRef = useRef<string>(uuid());

	const [
		{
			payload: triggerIssueCreatePayload,
			displayOnlyRequiredFields,
			disableCreationSuccessFlag,
			isSubTaskCreationOpen,
			isCreateLinkedIssue = false,
			isModalLoadInContext,
			triggerPointKey,
			interactedWithModalChangerButtons,
			inProgressIssueID,
			sessionId,
			wasStateRestored,
			jqlSwimlaneId,
			ecosystemCallback,
			product,
			actionSource,
			triggeredViaHotKey,
			addChildWorkItemTriggerSource,
			returnFocusRef,
		},
		{ setSessionId },
	] = useTriggerIssueCreateModal();

	useEffect(() => {
		if (!sessionId) {
			setSessionId(sessionIdRef.current);
		}
	}, [sessionId, setSessionId]);

	const { showEnforcementIfPendingToShow } = useStorageEnforcement();

	const [
		{
			isLoading: isModalLoading,
			fieldConfig: modalFieldConfig,
			fieldTabs: modalFieldTabs,
			contextFields: modalContextFields,
			meta: modalFieldsDataMeta,
			error: modalFieldsDataError,
			apiLatency: modalApiLatency,
			additionalServices: modalAdditionalServices,
		},
		fetchModalConfig,
	] = useModalLoadQuery({
		displayOnlyRequiredFields,
		triggerPointKey,
		isSubTaskCreationOpen,
		triggerIssueCreatePayload,
		isModalLoadInContext,
		...(fg('create_linked_issues_trigger_point_issue_view') ? { isCreateLinkedIssue } : {}),
	});

	const classificationFieldId = Object.keys(modalFieldConfig).find(
		(key) => modalFieldConfig[key].gqlType === 'JiraDataClassificationField',
	);

	const classificationFieldConfig = classificationFieldId
		? modalFieldConfig[classificationFieldId]
		: undefined;

	const proformaConfiguration = modalAdditionalServices?.proformaConfiguration;
	const storageLimitsData = modalAdditionalServices?.storageLimitsData;

	const { publish } = useGICLifeCycleEvents();

	const dirtyFieldsCurrentSession = useRef<DirtyFields>({});
	const getFormState = useRef<() => FormState | null>(() => null);

	const { data: projectContext } = useProjectContext();

	const [
		{
			isSubmitting,
			isOperationInProgress,
			shouldShowConfigureFieldModal,
			shouldShowFindFieldModal,
			selectedProject,
			selectedIssueType,
			meta,
			dataFetchError,
			isFormDirty: isCreateFormDirty,
			canCreateAnotherIssue,
			createdIssues,
		},
		{
			resetFormContext,
			toggleConfigureFieldsModalVisibility,
			toggleFindFieldsModalVisibility,
			setAreConfiguredFieldsModified,
			setForgeModules,
			setCurrentActiveTabIndex,
			setCanCreateAnotherIssue,
			addCreatedIssue,
		},
	] = useFormContext();
	const [, { resetState }] = useInlineFieldsActions();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	const { withMachineLearning } = useSmartFieldsMachineLearning();

	const [{ siteDomain, selectedCloudId }] = useSitePickerContext();
	const storageVariables = useStorageVariables();

	const shouldDisableActions = dataFetchError !== null;

	useEffect(() => {
		if (shouldDisableActions && setShouldRestore) {
			if (selectedProject === null) {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'noselectedproject',
					action: 'error',
				});
				fireUIAnalytics(analyticsEvent, 'createIssueViewError');
			}
			setShouldRestore(false);
		}
	}, [shouldDisableActions, setShouldRestore, selectedProject, createAnalyticsEvent]);

	const initFetchConfig = useCallback(() => {
		// ideally the change should be for all queries, but limitting the scope of change for now. The variable is defaulted to "STANDARD" in the BE.
		const issueCreateType =
			isCreateLinkedIssue &&
			isModalLoadInContext &&
			fg('create_linked_issues_trigger_point_issue_view')
				? {
						issueCreateType: isCreateLinkedIssue
							? ISSUE_CREATE_TYPE.linkedIssue
							: ISSUE_CREATE_TYPE.standard,
					}
				: {};

		if (!triggerIssueCreatePayload) {
			fetchModalConfig({
				variables: {
					...getQueryVariables(getIssueCreateProjectForMount(projectContext), null),
					...storageVariables,
				},
			});
		} else {
			const { customFieldsToPreserveOnFormRecreation = [], ...mainPayload } =
				triggerIssueCreatePayload;
			const parentId = mainPayload.parentId || null;
			const additionalContext = mainPayload.additionalContext || null;

			const projectPayload = isEmpty(mainPayload.project) ? null : mainPayload.project;

			fetchModalConfig({
				variables: {
					project: projectPayload,
					issueType: mainPayload.issueType || null,
					requestType: mainPayload.requestType || null,
					defaultValues: mainPayload.defaultValues || null,
					issueTypes: mainPayload.issueTypes || null,
					requestTypes: mainPayload.requestTypes || null,
					parentId,
					additionalContext,
					withNumberFormat: fg('number-format-in-gic'),
					...storageVariables,
					...issueCreateType,
				},
			});

			dirtyFieldsCurrentSession.current = {
				...dirtyFieldsCurrentSession.current,
				...Object.fromEntries(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					customFieldsToPreserveOnFormRecreation.map((k: any) => [k, true]),
				),
			};
		}
	}, [
		isCreateLinkedIssue,
		isModalLoadInContext,
		triggerIssueCreatePayload,
		fetchModalConfig,
		projectContext,
		storageVariables,
	]);

	const [
		{
			isLoading: isTransitionsLoading,
			error: transitionsError,
			transitions,
			fieldRequiredValidators,
		},
		fetchTransitions,
	] = useFetchTransitionsService();

	const isShowOnlyRequiredFieldsExperimentEnabled = useShowOnlyRequiredFieldsExperiment();

	const shouldShowStatus = useMemo(
		() => shouldShowStatusField(selectedProject?.projectType, modalFieldConfig),
		[selectedProject, modalFieldConfig],
	);
	const { resetAndReloadCreateForm } = useIssueCreateFromAnywhereContext();

	const refetchTransitions = useCallback(async () => {
		if (shouldShowStatus && selectedProject && selectedIssueType && !resetAndReloadCreateForm) {
			await fetchTransitions({
				projectId: selectedProject.value,
				issueTypeId:
					selectedIssueType.type === REQUEST_TYPE
						? selectedIssueType.issueTypeId
						: selectedIssueType.value,
				selectedCloudId,
				...(isShowOnlyRequiredFieldsExperimentEnabled && { expand: 'fieldRequiredValidators' }),
			});
		}
	}, [
		fetchTransitions,
		resetAndReloadCreateForm,
		selectedCloudId,
		selectedIssueType,
		selectedProject,
		shouldShowStatus,
		isShowOnlyRequiredFieldsExperimentEnabled,
	]);

	useEffect(() => {
		refetchTransitions();
	}, [
		fetchTransitions,
		selectedProject,
		selectedIssueType,
		shouldShowStatus,
		selectedCloudId,
		resetAndReloadCreateForm,
		refetchTransitions,
	]);

	const extraContext = useMemo(
		() => ({
			issueType: selectedIssueType?.label,
			projectKey: selectedProject?.projectKey,
			classificationTags: selectedProject?.classificationTags,
		}),
		[selectedProject, selectedIssueType],
	);

	const { customFields, filteredOutModules, loadingStatus } = useFetchForgeModules(extraContext);

	const hasForgeFinishedLoading = loadingStatus === 'success' || loadingStatus === 'error';

	useEffect(() => {
		if (selectedProject && selectedIssueType) {
			setForgeModules(customFields, filteredOutModules, hasForgeFinishedLoading);
		}
	}, [
		setForgeModules,
		customFields,
		hasForgeFinishedLoading,
		filteredOutModules,
		selectedProject,
		selectedIssueType,
		shouldShowStatus,
	]);
	const [showIssuesCreated, setShowIssuesCreated] = useState(false);
	const [showOnlyRequiredFields, setShowOnlyRequiredFields] = useState(false);

	const isApiCallTriggered = useRef(false);
	if (!isApiCallTriggered.current) {
		initFetchConfig();
		inProgressIssueID &&
			publish(GIC_LIFECYCLE_EVENTS.MODAL_OPENED, {
				inProgressIssueID,
			});

		const restoredCreatedIssuesExists = createdIssues?.length > 0;
		restoredCreatedIssuesExists && setShowIssuesCreated(restoredCreatedIssuesExists);

		isApiCallTriggered.current = true;
	}

	const [
		{
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onIssueCreate,
			onSiteSelection,
			onProjectSelection,
			onClose,
			displayCreateAnotherIssueToggle,
			callbackPayload,
			triggerButtonRef,
		},
		{ closeIssueCreateModal },
	] = useTriggerIssueCreateModal();

	const route = useCurrentRoute();

	const isCompanyManagedBoard =
		route?.name === 'rapidboard-board' ||
		route?.name === 'rapidboard-board-settings' ||
		route?.name === 'rapidboard-user-board' ||
		route?.name === 'rapidboard-user-board-settings' ||
		route?.name === 'servicedesk-boards';

	const isCompanyManagedBacklog = route?.name === 'rapidboard-backlog';

	const isCompanyManagedBoardOrBacklog = fg('show-create-success-flag-once-in-backlog-page')
		? isCompanyManagedBoard || isCompanyManagedBacklog
		: isCompanyManagedBoard;

	const isTeamManagedBoard =
		isCreateLinkedIssue &&
		fg('jracloud-93658-two-success-flag-triggered') &&
		route?.name === 'software-boards';

	const closeIssueCreateModalNew = useCallback(
		(issuesCreated: CreatedIssues) => {
			closeIssueCreateModal(Boolean(issuesCreated?.length));
			inProgressIssueID &&
				publish(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, {
					callbackPayload,
					inProgressIssueID,
					createdIssues: issuesCreated,
				});

			if (isCompanyManagedBoardOrBacklog) {
				setTimeout(() => {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, jira/jira-ssr/no-unchecked-globals-usage
					const activeElement = document.activeElement as HTMLElement;

					if (activeElement) {
						activeElement.blur();
					}
				}, 0);
			}
		},
		[
			callbackPayload,
			closeIssueCreateModal,
			inProgressIssueID,
			isCompanyManagedBoardOrBacklog,
			publish,
		],
	);

	if (isGICPositioningEnabled()) {
		// combine hook with above when cleaning up gic-refetch-on-discard_cxard
		const [{ shouldInitFetchConfig }, { resetShouldInitFetchConfig }] =
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal();
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (shouldInitFetchConfig || resetAndReloadCreateForm) {
				resetShouldInitFetchConfig();
				initFetchConfig();
			}
		}, [
			initFetchConfig,
			resetShouldInitFetchConfig,
			shouldInitFetchConfig,
			resetAndReloadCreateForm,
		]);
	}

	useEffect(() => {
		if (resetAndReloadCreateForm) {
			resetFormContext();
		}
	}, [resetAndReloadCreateForm, resetFormContext]);

	const sourceThatFiredModalDismiss = useRef<unknown>(null);

	const [objectId, setObjectId] = useState<string | undefined>(undefined);

	const { showFlag } = useFlagsService();

	const showSuccessFlag = useCallback(
		(issueList: CreatedIssues, successFlagParams?: SuccessFlagParams) => {
			if (issueList?.length && !disableCreationSuccessFlag) {
				const flagId = toFlagId(uuid());
				const issueDiscoveryUrl = getIssueDiscoveryUrl(issueList);
				showFlag({
					id: flagId,
					render: (flagProps: FlagCustomRenderProps) => {
						const successFlagProps = {
							key: flagProps.key,
							flagKey: flagProps.key,
							id: flagId,
							issueList,
							issueDiscoveryUrl,
						};
						return <SuccessFlag {...successFlagProps} successFlagParams={successFlagParams} />;
					},
				});

				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'flag',
					action: 'viewed',
				});
				fireUIAnalytics(analyticsEvent, 'createIssueSuccessNotification');

				setTimeout(() => {
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, @typescript-eslint/consistent-type-assertions
					const succeessFlagDismissButton = document.querySelector(
						'[data-testid="platform.ui.flags.common.ui.common-flag-v2-dismiss"]',
					) as HTMLButtonElement;

					succeessFlagDismissButton?.focus();
				}, 0);
			}
		},
		[createAnalyticsEvent, disableCreationSuccessFlag, showFlag],
	);

	useEffect(() => {
		const api = {
			// @ts-expect-error - TS7006 - Parameter 'params' implicitly has an 'any' type.
			renderSuccessFlag: (params) => {
				// @ts-expect-error - TS7006 - Parameter 'issue' implicitly has an 'any' type.
				const issueList = params.issues.map((issue) => ({
					issueId: issue.issueId,
					issueKey: issue.issueKey,
				}));
				successFlagDisplay.stop();
				showSuccessFlag(issueList, params);
			},
		};

		if (isCompanyManagedBoardOrBacklog) {
			// eslint-disable-next-line jira/wrm/no-load-bridge
			loadBridge({ name: 'jira-agile/rapid/ui/gic-success-flag/bridge' }).then((bridge) =>
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				bridge(api),
			);
		}
	}, [showSuccessFlag, isCompanyManagedBoardOrBacklog]);

	// combine this with above when cleaning up gic.positioning
	const [{ minimizableModalView }] = isGICPositioningEnabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal()
		: [{ minimizableModalView: undefined }];

	const [{ shouldPushMiniModalDiscoverability }] = useTriggerIssueCreateModal();

	const { updateView } = usePersistentIssueCreateModalViewResource();

	const performCleanUp = useCallback(
		(shouldPublishCreatedIssues = false, issuesCreated?: CreatedIssues) => {
			const issueList: CreatedIssues = issuesCreated ?? createdIssues ?? [];

			if (!isCompanyManagedBoardOrBacklog && !isTeamManagedBoard) {
				showSuccessFlag(issueList);
			} else {
				successFlagDisplay.start();
			}

			const isMobileExperienceEnabled = isMobileAndInMvpOrExistingUsersExperiment();

			closeIssueCreateModalNew(issueList);
			if (issuesCreated && !isMobileExperienceEnabled) {
				updateView(minimizableModalView === 'mini' ? 'mini' : 'modal');
			}

			if (isCompanyManagedBoardOrBacklog) {
				setTimeout(
					() => {
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, @typescript-eslint/consistent-type-assertions
						const succeessFlagDismissButton = document.querySelector(
							'[data-testid="platform.ui.flags.common.ui.common-flag-v2-dismiss"]',
						) as HTMLButtonElement;

						succeessFlagDismissButton?.focus();
					},
					isCompanyManagedBoard ? 2000 : 1000,
				);
			}

			if (shouldPublishCreatedIssues) {
				publishCreatedIssues({
					createdIssues: issueList,
					globalEventNamespaces,
					fieldsToAddInPublishedData,
					alertsToBeLinked,
					isSessionComplete: true,
					onIssueCreate: null,
					onClose,
					triggerPointKey,
					publishGICLifecycle: publish,
					inProgressIssueID,
					callbackPayload,
					selectedCloudId,
					jqlSwimlaneId,
					ecosystemCallback,
				});
			}
		},
		[
			createdIssues,
			isTeamManagedBoard,
			isCompanyManagedBoardOrBacklog,
			closeIssueCreateModalNew,
			showSuccessFlag,
			updateView,
			minimizableModalView,
			isCompanyManagedBoard,
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onClose,
			triggerPointKey,
			publish,
			inProgressIssueID,
			callbackPayload,
			selectedCloudId,
			jqlSwimlaneId,
			ecosystemCallback,
		],
	);

	const onCreateAnotherChange = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		setCanCreateAnotherIssue(!canCreateAnotherIssue);

		fireUIAnalytics(analyticsEvent, 'createAnotherIssue', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const handleTabIndexEmptyContainingRequiredField = () => {
		try {
			const fieldValues = getFormState.current()?.values || null;

			// Determine which tabs to use based on experiment and required fields filter
			let tabsToUse = modalFieldTabs;

			if (isShowOnlyRequiredFieldsExperimentEnabled && showOnlyRequiredFields && modalFieldConfig) {
				// Create filtered tabs showing only required fields (same logic as CreateForm)
				const filteredTabs = modalFieldTabs
					.map((tab) => {
						const filteredItems = tab.items.filter((fieldId) => {
							const field = modalFieldConfig[fieldId];
							return field?.isRequired === true;
						});

						const filteredCollapsedItems =
							tab.collapsedItems?.filter((fieldId) => {
								const field = modalFieldConfig[fieldId];
								return field?.isRequired === true;
							}) || [];

						return {
							...tab,
							items: filteredItems,
							collapsedItems: filteredCollapsedItems,
						};
					})
					.filter(
						(tab) => tab.items.length > 0 || (tab.collapsedItems && tab.collapsedItems.length > 0),
					);

				// Use filtered tabs for error checking when experiment is enabled
				tabsToUse = filteredTabs;
			}

			const tabIndex: number = getTabIndexWithError(tabsToUse, fieldValues, modalFieldConfig);
			setCurrentActiveTabIndex(tabIndex);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					packageName: 'jiraIssueCreate',
					id: 'gicModalTabChangeOnSubmit',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const handleEmptyAndRequiredFields = () => {
		try {
			const fieldValues: FormFieldValues | null = getFormState.current()?.values || null;

			let emptyRequiredFields: string[] = [];
			if (modifiedFieldConfig) {
				emptyRequiredFields = Object.keys(modifiedFieldConfig)?.filter(
					(fieldId: string) =>
						Boolean(modifiedFieldConfig[fieldId].isRequired) &&
						fieldValues &&
						isEmpty(fieldValues[fieldId]),
				);
			}

			const firstFieldIdWithError = getFirstFieldIdWithError({
				fields: Object.keys(fieldValues || {}),
				emptyRequiredFields,
			});

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			scrollToField(firstFieldIdWithError as string);

			firstFieldIdWithError && setFocusOnField(firstFieldIdWithError);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					packageName: 'jiraIssueCreate',
					id: 'gicModalNextRequiredFieldFocusOnSubmit',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const { atlassianAccountId } = useTenantContext();

	const onSubmit = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		handleTabIndexEmptyContainingRequiredField();
		handleEmptyAndRequiredFields();

		fireUIAnalytics(analyticsEvent, 'createIssue', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
			gicSessionId: sessionIdRef.current,
		});
	};

	const showWatchersWarningOrErrorFlag = useCallback(
		(createdIssue: CreatedIssue, response?: IssueCreateResponse['watchers']) => {
			const config = getWatchersFlagConfigFromResponse(
				response,
				createdIssue.issueKey,
				selectedIssueType?.label,
			);
			if (config) {
				/**
				 * Showing the watchers warning/error flag slightly after the issue-create-success-flag
				 * so they visually appear one after another in order and the user would be able to
				 * actually see the success flag sliding in first and then being pushed down.
				 */
				setTimeout(() => {
					showFlag(config);
				}, 600);
			}
		},
		[showFlag, selectedIssueType],
	);

	const onIssueCreateSuccess = useCallback(
		(createdIssue: CreatedIssue, watchersResponse?: IssueCreateResponse['watchers']): void => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const issuesCreated = addCreatedIssue(createdIssue)!;

			setObjectId(String(createdIssue?.issueId));
			setShowIssuesCreated(showIssuesCreated || canCreateAnotherIssue);

			publishCreatedIssues({
				createdIssues: [createdIssue],
				globalEventNamespaces,
				fieldsToAddInPublishedData,
				alertsToBeLinked,
				onIssueCreate,
				siteDomain,
				selectedCloudId,
				onClose,
				inProgressIssueID,
				publishGICLifecycle: publish,
				isIssueCreateSuccess: true,
				callbackPayload,
				jqlSwimlaneId,
				ecosystemCallback,
			});

			if (!canCreateAnotherIssue) {
				performCleanUp(true, issuesCreated);
			}

			showWatchersWarningOrErrorFlag(createdIssue, watchersResponse);
		},
		[
			addCreatedIssue,
			showIssuesCreated,
			canCreateAnotherIssue,
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onIssueCreate,
			siteDomain,
			selectedCloudId,
			onClose,
			inProgressIssueID,
			publish,
			callbackPayload,
			jqlSwimlaneId,
			ecosystemCallback,
			showWatchersWarningOrErrorFlag,
			performCleanUp,
		],
	);

	const showConfirmDismissModal = (source: string): void => {
		sourceThatFiredModalDismiss.current = source;
	};

	const hideConfirmDismissModal = (): void => {
		sourceThatFiredModalDismiss.current = null;
	};

	const onModalClose = (
		_: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
		source: string,
	): void => {
		fireUIAnalytics(analyticsEvent, source, {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
		showEnforcementIfPendingToShow();
		performCleanUp(true);
		resetState();
	};

	const onModalCloseWithConfirm =
		(source: string) =>
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
			if (!isCreateFormDirty) {
				setTimeout(() => {
					const activeElement = globalThis.document.activeElement;
					if (activeElement instanceof HTMLElement) {
						activeElement.blur();
					}

					if (returnFocusRef?.current && fg('alx-10155-focus-issues')) {
						returnFocusRef.current.focus();
					} else {
						triggerButtonRef?.focus();
					}
				}, 500);
				onModalClose(_, analyticsEvent, source);
				return;
			}
			showConfirmDismissModal(source);
		};

	const onConfirmDismissModalCancel = () => {
		hideConfirmDismissModal();
	};

	const onConfirmDismissModalConfirm = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		const source = (sourceThatFiredModalDismiss && sourceThatFiredModalDismiss.current) || '';

		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
		onModalClose(_, analyticsEvent, source);

		if (fg('fix-gryf-a11y-issues')) {
			setTimeout(() => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, @typescript-eslint/consistent-type-assertions
				const activeElement = document.activeElement as HTMLElement;
				if (activeElement) {
					activeElement?.blur?.();
				}

				if (returnFocusRef?.current && fg('alx-10155-focus-issues')) {
					returnFocusRef.current.focus?.();
				} else {
					triggerButtonRef?.focus?.();
				}
			}, 500);
		}
	};

	const [resetFormFieldsCounter, setResetFormFieldsCounter] = useState(0);
	const incrementResetCounter = useCallback(() => {
		setResetFormFieldsCounter((n) => n + 1);
	}, []);

	const hideConfigureFieldsModal = (): void => {
		toggleConfigureFieldsModalVisibility(false);
	};

	const hideFindFieldModal = (): void => {
		toggleFindFieldsModalVisibility(false);
	};

	const { shouldShowChangerSpotlight, onOpenSpotlight, onCloseSpotlight } =
		isGICPositioningEnabled()
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useChangerSpotlight(minimizableModalView)
			: {};

	// resetting form context should happen after the component is unmounted (if done before, certain components/fields were throwing error)
	useEffect(
		() => () => {
			resetFormContext();
		},
		[resetFormContext],
	);

	// add sprint field to published data to enable dynamic success flags for scrum projects
	useEffect(() => {
		const sprintFieldId = Object.keys(modalFieldConfig).find(
			(key) => modalFieldConfig[key].gqlType === 'JiraSprintField',
		);
		if (sprintFieldId !== undefined) {
			fieldsToAddInPublishedData.push(sprintFieldId);
		}
	}, [fieldsToAddInPublishedData, modalFieldConfig]);

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const fieldPropsExtensions = isGICPositioningEnabled()
		? {
				description: {
					onFocus: onOpenSpotlight,
					onBlur: onCloseSpotlight,
				},
			}
		: undefined;

	const preselectedStatus = triggerIssueCreatePayload?.status?.statusId;

	const classificationFieldProps: ClassificationFieldProps =
		useClassificationFieldProps(classificationFieldConfig);

	// Create modified field config with required validation for fieldRequiredValidators
	const modifiedFieldConfig = useMemo(() => {
		if (!fieldRequiredValidators || fieldRequiredValidators.length === 0) {
			return modalFieldConfig;
		}

		const modified = { ...modalFieldConfig };
		fieldRequiredValidators.forEach((fieldId) => {
			if (modified[fieldId]) {
				modified[fieldId] = {
					...modified[fieldId],
					isRequired: true,
				};
			}
		});

		return modified;
	}, [modalFieldConfig, fieldRequiredValidators]);

	// Force form re-render key when fieldRequiredValidators changes
	const formRenderKey = useMemo(() => {
		return `${selectedProject?.value}-${selectedIssueType?.value}-${fieldRequiredValidators?.join(',') || 'no-validators'}`;
	}, [selectedProject?.value, selectedIssueType?.value, fieldRequiredValidators]);

	const buildCreateFormProps = useCallback(
		(): CreateFormProps => ({
			isLoading: isModalLoading,
			fieldConfig: modifiedFieldConfig,
			fieldTabs: modalFieldTabs,
			contextFields: modalContextFields,
			meta: modalFieldsDataMeta,
			error: modalFieldsDataError,
			apiLatency: modalApiLatency,
			onFetchConfig: fetchModalConfig,
			onIssueCreateSuccess,
			onSiteSelection,
			onProjectSelection,
			canCreateAnotherIssue,
			dirtyFieldsCurrentSession,
			getFormState,
			onFormFieldsReset: incrementResetCounter,
			fieldPropsExtensions,
			onResetApolloCache,
			filteredOutModules,
			storageLimitsData,
			formRenderKey, // Add the render key to force re-render
			...(classificationFieldConfig &&
			Array.isArray(classificationFieldConfig?.allowedValues) &&
			classificationFieldConfig.allowedValues.length > 0
				? {
						classificationFieldProps,
					}
				: {}),
			...(shouldShowStatus
				? {
						statusFieldProps: {
							preselectedTransition: transitions?.find(
								(transition: StatusTransition) => transition.to.id === preselectedStatus,
							),
							initialTransition: transitions?.find((it: StatusTransition) => it.isInitial),
							availableTransitions: transitions,
							isTransitionsLoading,
							transitionsError,
						},
					}
				: Object.freeze({})),
			proformaConfiguration,
			watchersConfigurations: modalAdditionalServices.watchersConfigurations,
			onStatusCreatedOrUpdated: refetchTransitions,
			onShowOnlyRequiredFieldsChange: setShowOnlyRequiredFields,
		}),
		[
			isModalLoading,
			modifiedFieldConfig,
			modalFieldTabs,
			modalContextFields,
			modalFieldsDataMeta,
			modalFieldsDataError,
			modalApiLatency,
			fetchModalConfig,
			onIssueCreateSuccess,
			onSiteSelection,
			onProjectSelection,
			canCreateAnotherIssue,
			incrementResetCounter,
			fieldPropsExtensions,
			onResetApolloCache,
			filteredOutModules,
			storageLimitsData,
			classificationFieldConfig,
			classificationFieldProps,
			shouldShowStatus,
			transitions,
			isTransitionsLoading,
			transitionsError,
			proformaConfiguration,
			modalAdditionalServices.watchersConfigurations,
			refetchTransitions,
			preselectedStatus,
			formRenderKey, // Add to dependencies
			setShowOnlyRequiredFields,
		],
	);

	const createFormProps = useMemo(() => {
		let formProps = buildCreateFormProps();
		if (onTransformCreateFormProps) {
			formProps = onTransformCreateFormProps(formProps);
		}
		return formProps;
	}, [buildCreateFormProps, onTransformCreateFormProps]);

	const renderFormOrError = () => {
		if (shouldDisableActions) {
			fireErrorAnalytics({
				meta: {
					packageName: 'jiraIssueCreate',
					id: 'errorScreenRendered',
					teamName: 'gryffindor',
				},
				attributes: {
					...getAdditionalAttributes(),
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return <ErrorState />;
		}

		return <CreateForm {...createFormProps} />;
	};

	const currentUIModificationsConsentState = useUIModificationsConsentStatus();

	const getShouldDisableSubmit = (): boolean =>
		currentUIModificationsConsentState === UIM_CONSENT_REQUIRED ||
		!meta.areRequiredFieldsSupported ||
		shouldDisableActions ||
		!isEmpty(meta.queryErrorMessage);

	// initialise the request type fields layout preference on modal load
	// internally, this will not re-fetch if previously done
	const [{ data: isRequestTypeFieldsLayoutEnabled }, { initialise }] =
		useRequestTypeFieldsLayoutPreference();
	useEffect(() => {
		if (atlassianAccountId) {
			initialise(atlassianAccountId, createAnalyticsEvent);
		}
	}, [atlassianAccountId, createAnalyticsEvent, initialise]);

	const isRequestTypeSelected = selectedIssueType?.type === REQUEST_TYPE;

	const renderConfigureFieldsModal = () =>
		shouldShowConfigureFieldModal && selectedProject && selectedIssueType ? (
			<ConfigureFieldsModal
				onCancel={hideConfigureFieldsModal}
				project={{
					projectId: selectedProject.value,
					projectType: selectedProject.projectType,
				}}
				issueTypeId={
					isRequestTypeSelected ? selectedIssueType.issueTypeId : selectedIssueType.value
				}
				requestTypeKey={isRequestTypeSelected ? selectedIssueType.requestTypeKey : undefined}
				parentId={triggerIssueCreatePayload?.parentId?.parentId}
				setAreConfiguredFieldsModified={setAreConfiguredFieldsModified}
				onlyDisplayAllFieldsOption={isRequestTypeFieldsLayoutEnabled && isRequestTypeSelected}
				messageContent={isRequestTypeSelected ? <JsmRequestTypeFieldsChangeboarding /> : undefined}
			/>
		) : null;

	const renderFindFieldModal = () =>
		shouldShowFindFieldModal && selectedProject && selectedIssueType ? (
			<FindFieldModal
				project={{
					projectId: selectedProject.value,
					nextGenProject: selectedProject.isSimplified,
					projectType: selectedProject.projectType,
				}}
				issueTypeId={
					selectedIssueType.type === REQUEST_TYPE
						? selectedIssueType.issueTypeId
						: selectedIssueType.value
				}
				operation="CREATE"
				onCancel={hideFindFieldModal}
			/>
		) : null;

	const renderExistingGicWarningModal = () => <ExistingGICWarning />;

	const renderDisabledRestoreNavigationPrompt = () => (
		<DisabledRestoreNavigationPrompt setShouldRestore={setShouldRestore} />
	);

	const modalWrapper = (
		<ModalWrapper
			onModalCloseWithConfirm={onModalCloseWithConfirm}
			shouldDisableActions={shouldDisableActions}
			onSubmit={onSubmit}
			isSubmitting={isSubmitting}
			isOperationInProgress={isOperationInProgress}
			canCreateAnotherIssue={canCreateAnotherIssue}
			onCreateAnotherChange={onCreateAnotherChange}
			getShouldDisableSubmit={getShouldDisableSubmit}
			createdIssues={createdIssues}
			displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
			shouldShowChangerSpotlight={
				isGICPositioningEnabled()
					? !interactedWithModalChangerButtons && shouldShowChangerSpotlight
					: undefined
			}
			showIssuesCreated={showIssuesCreated}
			initialSummaryValue={
				isEmpty(createFormProps.fieldConfig)
					? null
					: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(createFormProps.fieldConfig?.summary?.defaultValue as string) || ''
			}
			projectType={selectedProject?.projectType}
			selectedIssueType={selectedIssueType}
			setShouldRestore={setShouldRestore}
			returnFocusRef={returnFocusRef}
			watchersConfigurations={modalAdditionalServices.watchersConfigurations}
			{...(isCreateFormDirty && selectedProject
				? {
						dismissModalEntryPointWrapper: (p) => (
							<DismissModalEntryPointWrapper
								{...p}
								entryPointProps={{
									project: {
										projectId: selectedProject.value,
										projectType: selectedProject.projectType,
									},
									sessionId: sessionIdRef.current,
									onClose: onConfirmDismissModalCancel,
									onConfirm: onConfirmDismissModalConfirm,
								}}
							/>
						),
					}
				: {})}
		>
			{!isGICPositioningEnabled() && <MountEvent onMount={fireScreenAnalyticsDeferred} />}
			{renderFormOrError()}
			<UiModificationsEntryPoint
				issueTypeFieldType={modalContextFields.issuetype?.type || ''}
				modalFieldConfig={modalFieldConfig}
				resetFormFieldsCounter={resetFormFieldsCounter}
				selectedIssueType={selectedIssueType}
				selectedProject={selectedProject}
				triggerPointKey={triggerPointKey}
			/>
			{renderConfigureFieldsModal()}
			{renderFindFieldModal()}
			{isGICPositioningEnabled() && renderExistingGicWarningModal()}
			{renderDisabledRestoreNavigationPrompt()}
		</ModalWrapper>
	);

	return (
		<ContextualAnalyticsData
			sourceType={MODAL}
			sourceName="createIssue"
			containerType="project"
			objectType="issue"
			objectId={objectId}
			containerId={selectedProject ? selectedProject.value : ''}
			attributes={{
				isNewGIC: true,
				isCreatingAnotherIssue: canCreateAnotherIssue,
				isSubtask: isSubTaskCreationOpen,
				triggerPointKey,
				projectType: selectedProject?.projectType,
				...(selectedProject ? { nextGenProject: selectedProject.isSimplified } : {}),
				...(isGICPositioningEnabled() ? { gicViewState: minimizableModalView } : {}),
				...(fg('create_linked_issues_trigger_point_issue_view') ? { isCreateLinkedIssue } : {}),
				hasProformaForm: !!proformaConfiguration?.hasProformaForm,
				wasStateRestored,
				isDiscoverabilityPush:
					!!shouldPushMiniModalDiscoverability && isPartOfDiscoverabilityTriggers(triggerPointKey),
				smartFieldsEnabled: withMachineLearning,
				sessionId: !sessionId ? sessionIdRef.current : sessionId,
				...(fg('quick_actions_with_hotkeys_ga') ? { triggeredViaHotKey } : {}),
				...(product ? { product } : {}),
				...(actionSource ? { actionSource } : {}),
				...(addChildWorkItemTriggerSource ? { addChildWorkItemTriggerSource } : {}),
				...getAdditionalAttributes(),
			}}
		>
			<UiModificationsContainer>{modalWrapper}</UiModificationsContainer>
		</ContextualAnalyticsData>
	);
};

export default CreateModal;
